import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TID,
  CONTACT_EMAILS,
  CONTACT_PHONES,
  WHATSAPP_NUMBER,
} from "@/lib/testIds";
import TextReveal from "./TextReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Government Subsidy",
  "Debt Recovery/Arbitration",
  "Project Funding",
  "Other",
];

const STATES = [
  "Haryana",
  "Punjab",
  "Delhi",
  "Uttar Pradesh",
  "Rajasthan",
  "Maharashtra",
  "Gujarat",
  "Karnataka",
  "Tamil Nadu",
  "Telangana",
  "Other",
];

const Field = ({ label, id, children, span = 6 }) => (
  <label
    htmlFor={id}
    className={`col-span-12 md:col-span-${span} block group`}
  >
    <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/50 group-focus-within:text-[color:var(--gold)] transition-colors">
      {label}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "w-full bg-transparent border-b border-white/25 py-3 text-white placeholder-white/30 focus:border-[color:var(--gold)] focus:outline-none transition-colors font-sans text-[15px]";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    state: "",
    service: "",
    message: "",
    website: "", // honeypot — hidden from users, must stay empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill Name, Phone, Email and Service.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Thanks — we'll be in touch within one working day.");
      setForm({
        name: "",
        company: "",
        phone: "",
        email: "",
        state: "",
        service: "",
        message: "",
        website: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or WhatsApp us.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={TID.contact.root}
      className="relative bg-navy text-white py-24 md:py-32 noise overflow-hidden"
    >
      <div aria-hidden className="absolute -top-40 left-1/4 h-[520px] w-[520px] rounded-full bg-[color:var(--violet)]/40 blur-[160px]" />
      <div aria-hidden className="absolute -bottom-40 right-10 h-[460px] w-[460px] rounded-full bg-[color:var(--gold)]/22 blur-[150px]" />

      <Toaster theme="dark" position="top-center" richColors />
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-6 pb-14 md:pb-20 border-b border-white/10">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[color:var(--gold)]">
              09 — Get started
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <TextReveal as="h2" className="font-serif tracking-tight text-4xl md:text-6xl leading-[1.02]">
              Tell us what you're building — or what's owed to you.
            </TextReveal>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-10 lg:gap-16 pt-14 md:pt-20">
          <div className="col-span-12 lg:col-span-4 space-y-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                Direct line
              </p>
              <div className="mt-3 space-y-2">
                {CONTACT_PHONES.map((p) => (
                  <a
                    key={p}
                    href={`tel:${p.replace(/\s+/g, "")}`}
                    className="block font-serif text-2xl md:text-3xl tracking-tight hover:text-[color:var(--gold)] transition-colors"
                  >
                    {p}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                Email
              </p>
              <div className="mt-3 space-y-2">
                {CONTACT_EMAILS.map((e) => (
                  <a
                    key={e}
                    href={`mailto:${e}`}
                    className="block font-serif text-xl md:text-2xl tracking-tight hover:text-[color:var(--gold)] transition-colors break-all"
                  >
                    {e}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                Or, message us on
              </p>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-3 inline-flex items-center gap-3 border border-white/20 px-5 py-3 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.24em]">
                  WhatsApp instantly
                </span>
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <div className="pt-6 border-t border-white/10 text-sm text-white/60 leading-relaxed">
              We reply within one working day. All conversations are treated as
              privileged and confidential.
            </div>
          </div>

          <div className="col-span-12 lg:col-span-8">
            {done ? (
              <motion.div
                data-testid={TID.contact.success}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-[color:var(--gold)]/40 p-10 md:p-14 bg-white/[0.03]"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold)]">
                  Filed · Reference logged
                </p>
                <h3 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight">
                  Thanks — we&rsquo;ve got it.
                </h3>
                <p className="mt-6 text-white/70 max-w-lg">
                  A member of our advisory team will reach out within one
                  working day. For anything urgent, tap WhatsApp or call the
                  direct line.
                </p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="mt-8 font-mono text-[11px] uppercase tracking-[0.24em] text-white/70 border-b border-white/30 pb-1 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]"
                >
                  Submit another enquiry
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-12 gap-x-6 gap-y-8">
                {/* Honeypot — hidden from users, bots often fill it */}
                <div aria-hidden className="hidden">
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={onChange("website")}
                    />
                  </label>
                </div>
                <Field label="Your name*" id={TID.contact.name}>
                  <input
                    id={TID.contact.name}
                    data-testid={TID.contact.name}
                    value={form.name}
                    onChange={onChange("name")}
                    className={inputCls}
                    placeholder="Full name"
                    required
                  />
                </Field>
                <Field label="Company" id={TID.contact.company}>
                  <input
                    id={TID.contact.company}
                    data-testid={TID.contact.company}
                    value={form.company}
                    onChange={onChange("company")}
                    className={inputCls}
                    placeholder="Company / Firm"
                  />
                </Field>
                <Field label="Phone*" id={TID.contact.phone}>
                  <input
                    id={TID.contact.phone}
                    data-testid={TID.contact.phone}
                    value={form.phone}
                    onChange={onChange("phone")}
                    className={inputCls}
                    placeholder="+91 —"
                    required
                  />
                </Field>
                <Field label="Email*" id={TID.contact.email}>
                  <input
                    id={TID.contact.email}
                    data-testid={TID.contact.email}
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    className={inputCls}
                    placeholder="you@company.com"
                    required
                  />
                </Field>

                <Field label="State" id={TID.contact.state}>
                  <Select
                    value={form.state}
                    onValueChange={(v) => setForm({ ...form, state: v })}
                  >
                    <SelectTrigger
                      id={TID.contact.state}
                      data-testid={TID.contact.state}
                      className="w-full bg-transparent border-0 border-b border-white/25 rounded-none py-3 h-auto text-white focus:border-[color:var(--gold)] focus:ring-0 focus:ring-offset-0 [&_svg]:text-white/50"
                    >
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="bg-navy text-white border-white/20">
                      {STATES.map((s) => (
                        <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-[color:var(--gold)]">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="What do you need help with?*" id={TID.contact.service}>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm({ ...form, service: v })}
                  >
                    <SelectTrigger
                      id={TID.contact.service}
                      data-testid={TID.contact.service}
                      className="w-full bg-transparent border-0 border-b border-white/25 rounded-none py-3 h-auto text-white focus:border-[color:var(--gold)] focus:ring-0 focus:ring-offset-0 [&_svg]:text-white/50"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-navy text-white border-white/20">
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={TID.contact.serviceOption(
                            s.toLowerCase().replace(/[^a-z]+/g, "-")
                          )}
                          className="focus:bg-white/10 focus:text-[color:var(--gold)]"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Your message" id={TID.contact.message} span={12}>
                  <textarea
                    id={TID.contact.message}
                    data-testid={TID.contact.message}
                    value={form.message}
                    onChange={onChange("message")}
                    rows={4}
                    className={`${inputCls} resize-none`}
                    placeholder="A short note about your project, subsidy, or dispute…"
                  />
                </Field>

                <div className="col-span-12 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/10">
                  <p className="text-xs text-white/50 max-w-sm">
                    By submitting, you agree to be contacted about your
                    enquiry. We do not share your details.
                  </p>
                  <button
                    type="submit"
                    data-testid={TID.contact.submit}
                    disabled={submitting}
                    className="inline-flex items-center gap-3 bg-[color:var(--gold)] px-7 py-4 text-navy hover:bg-white transition-colors disabled:opacity-70"
                  >
                    <span className="font-mono text-[11px] uppercase tracking-[0.24em]">
                      {submitting ? "Filing…" : "Submit enquiry"}
                    </span>
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowUpRight className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
